'use client';
/* eslint-disable @nx/enforce-module-boundaries */
import React, { useState, useEffect } from 'react';
import { useRef } from 'react';
import { ExitFullScreenIcon, FullScreenIcon } from '@mybridge/icons';
import { Box, HStack, Stack, Text, Button, Flex, Icon, VStack, Card } from '@mybridge/ui';
import { useContext } from 'react';
import { DiskContext } from 'v4/components/disk/context';
import { DiskListingGrid } from 'v4/components/disk/disk-listing-grid';
import { DiskListingTable } from 'v4/components/disk/disk-listing-table';
import { MyDiskListingHeader } from 'v4/components/disk/my-disk/listing-header';
import { SharedDiskUsersListingGrid } from 'v4/components/disk/shared-disk/disk-listing-grid';
import { SharedDiskUsersListingTable } from 'v4/components/disk/shared-disk/disk-listing-table';
import { TeamDiskListingGrid } from 'v4/components/disk/team-disk/disk-listing-grid';
import { TeamDiskListingTable } from 'v4/components/disk/team-disk/disk-listing-table';
// Removed DocViewer; using custom viewers per file type
import { isMobile, isTablet } from 'react-device-detect';
import dynamic from 'next/dynamic';

import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalFooter,
} from '@mybridge/ui/modal';
import { Input, InputGroup, InputLeftElement } from '@mybridge/ui/input';
import { SearchIcon } from '@mybridge/icons/Search';
import { MyBridgeCircleIcon } from '@mybridge/icons';
import { useDisclosure } from '@mybridge/ui';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { Spinner, Tooltip } from '@chakra-ui/react';
import { CloseFileIcon } from '@mybridge/icons/Close';
import { ANALYTICS_EVENTS, trackEvent } from 'v4/utils/analyticsEvents';
import { useSelector } from 'react-redux';
import { getFullFileName } from 'v4/lib/commons';
const CustomPDFRenderer = dynamic(() => import('@mybridge/ui/pdf-renderer'), {
  ssr: false,
});

// Custom Audio Renderer for MP3 files
const CustomAudioRenderer = ({ mainState: { currentDocument } }) => {
  if (!currentDocument) return null;
  const { t } = useTranslator();
  
  return (
    <Box 
      display="flex" 
      flexDirection="column" 
      alignItems="center" 
      justifyContent="center" 
      width="600px"
      height="100%"
      p={6}
    >
      <Box 
        width="100%" 
        maxWidth="600px" 
        p={6} 
        borderRadius="lg" 
        bg="white" 
        boxShadow="0px 4px 12px rgba(0, 0, 0, 0.1)"
        display="flex"
        flexDirection="column"
        alignItems="center"
      >
        {/* Audio file icon */}
        <Box 
          width="120px" 
          height="120px" 
          borderRadius="full" 
          bg="gray.100" 
          display="flex" 
          alignItems="center" 
          justifyContent="center"
          mb={4}
        >
          <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 18V6L21 3V15" stroke="#718096" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M6 21C7.65685 21 9 19.6569 9 18C9 16.3431 7.65685 15 6 15C4.34315 15 3 16.3431 3 18C3 19.6569 4.34315 21 6 21Z" stroke="#718096" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M18 18C19.6569 18 21 16.6569 21 15C21 13.3431 19.6569 12 18 12C16.3431 12 15 13.3431 15 15C15 16.6569 16.3431 18 18 18Z" stroke="#718096" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </Box>
        
        {/* File name */}
        <Text 
          fontSize="xl" 
          fontWeight="bold" 
          mb={5} 
          textAlign="center"
          noOfLines={1}
          color="gray.700"
          width="100%"
        >
          {currentDocument.fileName || "Audio File"}
        </Text>
        
        {/* Audio player */}
        <Box 
          width="100%" 
          borderRadius="md" 
          bg="gray.50"
          p={3}
          border="1px solid"
          borderColor="gray.200"
        >
          <audio 
            controls 
            src={currentDocument.uri} 
            style={{ 
              width: '100%',
              height: '40px'
            }}
          >
            {t('myMessages.browserNoAudioSupport') || "Your browser does not support the audio element."}
          </audio>
        </Box>
      </Box>
    </Box>
  );
};

// Define supported file types for the audio renderer
CustomAudioRenderer.fileTypes = ['mp3', 'audio/mpeg', 'audio/mp3', 'wav', 'audio/wav', 'ogg', 'audio/ogg'];
CustomAudioRenderer.weight = 1;

export const MyDiskRightPanel = () => {
  const {
    view,
    selectedMainDirType,
    selectedMainDir,
    selectedSharedDiskUser,
    selectedFile,
    showPreview,
    setShowPreview,
    setSelectedFile,
    teamDiskMemberInfo,
    isFullScreen,
    setIsFullScreen
  } = useContext(DiskContext);
  const { userProfileInfo } = useSelector((state) => state.userProfile);

  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });
  const [viewerError, setViewerError] = useState(false);
  const [canPreview, setCanPreview] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [precheckAttempts, setPrecheckAttempts] = useState(0);
  const viewerRef = useRef(null);
  const { t } = useTranslator();
  
  // Update window dimensions on resize
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  // Observe DocViewer DOM to detect its internal error UI and replace with custom fallback
  useEffect(() => {
    if (!showPreview) return;
    setViewerError(false);
    const node = viewerRef.current;
    if (!node) return;
    let cancelled = false;
    const detectError = () => {
      if (!node) return false;
      const text = node.innerText || '';
      // Heuristic: detect DocViewer error verbiage
      if (text.includes('File not found') || text.includes('not publicly accessible')) {
        return true;
      }
      // Also look for elements commonly used by renderers to show errors
      const errEl = node.querySelector('[role="alert"], .error, .doc-viewer-error');
      return !!errEl;
    };
    const obs = new MutationObserver(() => {
      if (!cancelled && detectError()) {
        setViewerError(true);
      }
    });
    obs.observe(node, { childList: true, subtree: true, characterData: true });
    // Run an initial check shortly after mount to catch immediate render
    const t = setTimeout(() => {
      if (!cancelled && detectError()) setViewerError(true);
    }, 50);
    return () => {
      cancelled = true;
      clearTimeout(t);
      obs.disconnect();
    };
  }, [showPreview, selectedFile?.file]);

  useEffect(() => {
    if (showPreview && selectedFile) {
      trackEvent(ANALYTICS_EVENTS.DiskFilePreview, userProfileInfo?.slug || 'anonymous');
    }
  }, [showPreview, selectedFile, userProfileInfo?.slug]);
  
  const handleClosePreview = () => {
    setShowPreview(false);
    setSelectedFile();
    setIsFullScreen(false); // Make sure to reset full screen when closing
  };
  const sideDrawerDisc = useDisclosure();
  // Build full file URL if extension stored separately
  const buildFileUrl = () => {
    const base = selectedFile?.file || '';
    const ext = (selectedFile?.extension || '').toString().trim();
    if (!base) return '';
    if (!ext) return base;
    const lower = base.toLowerCase();
    if (lower.endsWith(`.${ext.toLowerCase()}`)) return base;
    return `${base}.${ext}`;
  };

  const fileUrl = buildFileUrl();
  const fileName = selectedFile?.display_name || selectedFile?.name || 'file';
  const fileExt = (selectedFile?.extension || '').toLowerCase();
  const fileMime = (selectedFile?.mime_type || '').toLowerCase();

  const isPdf = fileMime === 'application/pdf' || fileExt === 'pdf';
  const isAudio = ['mp3', 'wav', 'ogg'].includes(fileExt) || fileMime.startsWith('audio/');
  const isImage = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(fileExt) || fileMime.startsWith('image/');
  const isVideo = ['mp4', 'webm', 'ogg'].includes(fileExt) || fileMime.startsWith('video/');
  const isCsv = fileExt === 'csv' || fileMime === 'text/csv';
  const isXlsx = ['xlsx', 'xls'].includes(fileExt) || fileMime.includes('spreadsheet') || fileMime === 'application/vnd.ms-excel';
  const isText = ['txt', 'md', 'json'].includes(fileExt) || (fileMime.startsWith('text/') && !isCsv);

  // Helper function to check if URL already has extension
  // const hasExtensionInUrl = (url, extension) => {
  //   if (!url || !extension) return false;
  //   return url.toLowerCase().endsWith(`.${extension.toLowerCase()}`);
  // };

  // // Build the URI
  // const buildFileUri = () => {
  //   if (!selectedFile?.file) return '';

  //   if (selectedFile?.extension && hasExtensionInUrl(selectedFile.file, selectedFile.extension)) {
  //     return selectedFile.file;
  //   }
  //   if (selectedFile?.extension) {
  //     return `${selectedFile.file}.${selectedFile.extension}`;
  //   }
  //   return selectedFile.file;
  // };

  // const docs = [{ 
  //   uri: buildFileUri(),
  //   fileName: `${selectedFile?.display_name || selectedFile?.name}${selectedFile?.extension ? `.${selectedFile.extension}` : ''}`,
  //   fileType: selectedFile?.extension
  // }];

  // Calculate modal dimensions based on screen size and mode
  const getModalDimensions = () => {
    if (isFullScreen) {
      return {
        width: '100vw',
        height: '100vh',
        maxWidth: '100vw',
        maxHeight: '100vh',
        margin: 0,
        borderRadius: 0,
      };
    }
    
    if (isMobile) {
      return {
        width: '95vw',
        height: 'auto',
        maxWidth: '95vw',
        maxHeight: '80vh',
      };
    }
    
    return {
      width: 'auto',
      height: 'auto',
      maxWidth: '50%',
      maxHeight: '80vh',
      minWidth: '600px',
    };
  };

  // Calculate viewer height
  const getViewerHeight = () => {
    if (isFullScreen) {
      return 'calc(100vh - 60px)';
    }
    
    if (isMobile) {
      return '60vh';
    }
    
    return '70vh';
  };

  const modalDimensions = getModalDimensions();
  const viewerHeight = getViewerHeight();

  // Reset state on open/file change
  useEffect(() => {
    if (!showPreview) return;
    setViewerError(false);
    setCanPreview(false);
    setPrecheckAttempts(0);
  }, [showPreview, fileUrl]);

  // Pre-validate file accessibility to avoid flashes from internal errors
  useEffect(() => {
    if (!showPreview) return;
    if (!fileUrl) {
      setViewerError(true);
      return;
    }
    let parsed;
    try {
      parsed = new URL(fileUrl, typeof window !== 'undefined' ? window.location.href : undefined);
    } catch {
      setViewerError(true);
      return;
    }
    const isSameOrigin = typeof window !== 'undefined' && parsed.origin === window.location.origin;
    let cancelled = false;
    const controller = new AbortController();
    setIsChecking(true);
    (async () => {
      const tryHead = async () => {
        try {
          const res = await fetch(parsed.href, {
            method: 'HEAD',
            credentials: isSameOrigin ? 'include' : 'omit',
            mode: isSameOrigin ? 'same-origin' : 'cors',
            signal: controller.signal,
          });
          if (cancelled) return null;
          return res;
        } catch {
          return null;
        }
      };
      const tryRangeGet = async () => {
        try {
          const res = await fetch(parsed.href, {
            method: 'GET',
            headers: { Range: 'bytes=0-0' },
            credentials: isSameOrigin ? 'include' : 'omit',
            mode: isSameOrigin ? 'same-origin' : 'cors',
            signal: controller.signal,
          });
          if (cancelled) return null;
          return res;
        } catch {
          return null;
        }
      };
      const headRes = await tryHead();
      if (headRes && (headRes.ok || headRes.status === 405 || headRes.status === 206)) {
        if (!cancelled) { setCanPreview(true); setIsChecking(false); }
        return;
      }
      const getRes = await tryRangeGet();
      if (getRes && (getRes.ok || getRes.status === 206)) {
        if (!cancelled) { setCanPreview(true); setIsChecking(false); }
        return;
      }
      // Retry with backoff a few times to allow freshly uploaded files to become available
      if (!cancelled) {
        const nextAttempt = precheckAttempts + 1;
        if (nextAttempt <= 4) {
          setPrecheckAttempts(nextAttempt);
          const delay = Math.min(2000, 300 * Math.pow(2, nextAttempt));
          setTimeout(() => {
            if (!cancelled) {
              // trigger effect by updating dependency
              setIsChecking(false);
              setIsChecking(true);
            }
          }, delay);
        } else {
          setViewerError(true);
          setIsChecking(false);
        }
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [showPreview, fileUrl, precheckAttempts]);

  // CSV state and loader
  const [csvRows, setCsvRows] = useState([]);
  useEffect(() => {
    if (!showPreview || !canPreview || !isCsv) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(fileUrl);
        if (!res.ok) throw new Error('csv_fetch_failed');
        const text = await res.text();
        if (cancelled) return;
        const rows = [];
        // Minimal CSV parser (handles commas and simple quotes)
        const lines = text.split(/\r?\n/);
        for (const line of lines) {
          if (!line.trim()) continue;
          const cells = [];
          let cur = '';
          let inQuotes = false;
          for (let i = 0; i < line.length; i++) {
            const ch = line[i];
            if (ch === '"') {
              if (inQuotes && line[i + 1] === '"') {
                cur += '"';
                i++;
              } else {
                inQuotes = !inQuotes;
              }
            } else if (ch === ',' && !inQuotes) {
              cells.push(cur);
              cur = '';
            } else {
              cur += ch;
            }
          }
          cells.push(cur);
          rows.push(cells);
        }
        setCsvRows(rows);
      } catch (e) {
        if (!cancelled) setViewerError(true);
      }
    })();
    return () => { cancelled = true; };
  }, [showPreview, canPreview, isCsv, fileUrl]);

  // XLSX state and loader
  const [xlsxHtml, setXlsxHtml] = useState('');
  useEffect(() => {
    if (!showPreview || !canPreview || !isXlsx) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(fileUrl);
        if (!res.ok) throw new Error('xlsx_fetch_failed');
        const buf = await res.arrayBuffer();
        const XLSX = (await import('xlsx')).default || (await import('xlsx'));
        if (cancelled) return;
        const wb = XLSX.read(buf, { type: 'array' });
        const first = wb.SheetNames[0];
        const ws = wb.Sheets[first];
        const html = XLSX.utils.sheet_to_html(ws, { id: 'sheet-preview', editable: false });
        setXlsxHtml(html);
      } catch (e) {
        if (!cancelled) setViewerError(true);
      }
    })();
    return () => { cancelled = true; };
  }, [showPreview, canPreview, isXlsx, fileUrl]);

  return (
    <>
      <Stack p={isMobile? 0 : 4}  flex={1} {...(!isMobile && {
        position: "fixed",
        width: ["100%", "70%", "60%", "49%"],
      })}>
        <MyDiskListingHeader pb={2} />

        {/* Main content area */}
        <Box w="100%" flex={[1, '']}>
          {view === 'list' ? (
            selectedMainDirType === 'shared-disk' && !selectedSharedDiskUser ? (
              <SharedDiskUsersListingTable />
            ) : selectedMainDirType === 'team-disk' && !selectedMainDir?.id ? (
              <TeamDiskListingTable />
            ) : (
              <DiskListingTable accessTypeTeamAccess={teamDiskMemberInfo} />
            )
          ) : view === 'grid' ? (
            selectedMainDirType === 'shared-disk' && !selectedSharedDiskUser ? (
              <SharedDiskUsersListingGrid />
            ) : selectedMainDirType === 'team-disk' && !selectedMainDir ? (
              <TeamDiskListingGrid />
            ) : (
              <DiskListingGrid accessTypeTeamAccess={teamDiskMemberInfo} />
            )
          ) : null}
        </Box>

        {/* Document Viewer Modal */}
        {selectedFile?.type === 'F' && showPreview && fileUrl ? (
          <Modal
            isOpen={showPreview}
            onClose={handleClosePreview}
            isCentered={!isFullScreen}
            size="2xl"
            motionPreset="slideInBottom"
            autoFocus={false}
          >
            <ModalOverlay bg="rgba(0, 0, 0, 0.6)" />
            <ModalContent
              position={isFullScreen ? "fixed" : "relative"}
              top={isFullScreen ? "0" : undefined}
              left={isFullScreen ? "0" : undefined}
              {...modalDimensions}
              bg="white"
              boxShadow="0px 4px 20px rgba(0, 0, 0, 0.15)"
              overflow="hidden"
            >
              <ModalHeader
                bg="#f8f9fa"
                borderBottom="1px solid #e2e8f0"
                py={3}
                px={4}
              >
                <Flex justify="space-between" align="center">
                  <Text
                    fontSize={isMobile ? "sm" : "md"}
                    fontWeight="500"
                    noOfLines={1}
                    flex="1"
                    mr={2}
                  >
                    {/* {fileName}
                    {selectedFile?.extension && `.${selectedFile?.extension}`} */}
                    {getFullFileName(selectedFile)}
                  </Text>
                  <HStack spacing={2}>
                    <Tooltip label={isFullScreen ? "Exit full screen" : "Full screen"} placement='top' hasArrow>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setIsFullScreen(!isFullScreen)}
                      >{isFullScreen ? <ExitFullScreenIcon height="18px" width="18px" /> : <FullScreenIcon height="18px" width="18px" />}</Button>
                    </Tooltip>
                    <Tooltip label={t('buttons.close') || "Close"} placement='top' hasArrow>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleClosePreview}
                        leftIcon={<CloseFileIcon />}
                      />
                    </Tooltip>
                  </HStack>
                </Flex>
              </ModalHeader>

              <ModalBody p={0}>
                {viewerError ? (
                  <Box 
                    height={viewerHeight}
                    width="100%"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    p={6}
                  >
                    <VStack spacing={2} align="center" bg="white">
                      <img
                        src="/images/disk_preview_error_image.svg"
                        alt="No Preview"
                      />
                      <Text
                        fontSize="22px"
                        lineHeight="30px"
                        color="#AFAFAF"
                        maxW="425px"
                        textAlign="center"
                      >
                        {'Couldn’t preview file'}
                      </Text>
                      <Text
                        fontSize={15}
                        lineHeight="30px"
                        cursor="pointer"
                      >
                        {"Try again later"}
                      </Text>

                      {fileUrl ? (
                        <Button  
                          variant="primary"
                          borderRadius="30px" 
                          as="a" 
                          href={fileUrl} 
                          download 
                          color="white"
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          {t('profilePhotosVideos.download')||'Download'}
                        </Button>
                      ) : null}
                    </VStack>
                    {/* <Stack spacing={3} align="center">
                      <Text fontSize="lg" fontWeight="600">{t('myDisk.previewUnavailable') || 'Preview unavailable'}</Text>
                      <Text color="gray.600" textAlign="center">{t('myDisk.fileNotAccessible') || 'We could not access this file for preview. You can download it or try again later.'}</Text>
                      <HStack>
                        {fileUrl ? (
                          <Button as="a" href={fileUrl} download target="_blank" rel="noopener noreferrer">{t('buttons.download') || 'Download'}</Button>
                        ) : null}
                        <Button variant="ghost" onClick={handleClosePreview}>{t('buttons.close') || 'Close'}</Button>
                      </HStack>
                    </Stack> */}
                  </Box>
                ) : isChecking || !canPreview ? (
                  <Box 
                    height={viewerHeight}
                    width="100%"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    p={6}
                  >
                    <Spinner/>
                  </Box>
                ) : (
                <Box 
                  height={viewerHeight}
                  width="100%"
                  overflowY="auto"
                  overflowX='hidden'
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  {/* Custom viewers */}
                  {isPdf ? (
                    <Box w="100%" h="100%" px={2}>
                      <CustomPDFRenderer file={fileUrl} />
                    </Box>
                  ) : isAudio ? (
                    <CustomAudioRenderer mainState={{ currentDocument: { uri: fileUrl, fileName } }} />
                  ) : isImage ? (
                    <Box as="img" src={fileUrl} alt={fileName} maxH="100%" maxW="100%" onError={() => setViewerError(true)} />
                  ) : isVideo ? (
                    <Box w="100%" p={2}>
                      <video controls style={{ maxWidth: '100%', maxHeight: '100%' }} onError={() => setViewerError(true)}>
                        <source src={fileUrl} type={fileMime || undefined} />
                      </video>
                    </Box>
                  ) : isCsv ? (
                    <Box w="100%" h="100%" p={2} overflow="auto" bg="white" border="1px solid #e2e8f0" borderRadius="md">
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <tbody>
                          {csvRows.map((row, ri) => (
                            <tr key={ri}>
                              {row.map((cell, ci) => (
                                <td key={ci} style={{ border: '1px solid #e2e8f0', padding: '6px' }}>{cell}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Box>
                  ) : isXlsx ? (
                    <Box w="100%" h="100%" p={2} overflow="auto" bg="white" border="1px solid #e2e8f0" borderRadius="md">
                      {xlsxHtml ? (
                        <div dangerouslySetInnerHTML={{ __html: xlsxHtml }} />
                      ) : (
                        <Spinner/>
                      )}
                    </Box>
                  ) : isText ? (
                    <Box w="100%" h="100%" p={4} overflowY="auto" bg="white" border="1px solid #e2e8f0" borderRadius="md">
                      <iframe src={fileUrl} style={{ width: '100%', height: '100%', border: 'none' }} onError={() => setViewerError(true)} />
                    </Box>
                  ) : (
                    <Stack spacing={3} align="center" >
                       <Card
                  variant="thinBorderedCard"
                  bg="white"
                  // minH="85vh"
                  display="flex"
                  alignItems="center"
                  flexDirection="column"
                  pt={4}
                  margin="auto"
                >
                  <VStack spacing={2} align="center" bg="white">
                    <img
                      src="/images/disk_preview_error_image.svg"
                      alt="No Preview"
                    />
                    <Text
                      fontSize="22px"
                      lineHeight="30px"
                      color="#AFAFAF"
                      maxW="425px"
                      textAlign="center"
                    >
                      {'Couldn’t preview file'}
                    </Text>
                    <Text
                      fontSize={15}
                      lineHeight="30px"
                      cursor="pointer"
                    >
                      {"Try again later"}
                    </Text>
                  </VStack>
                </Card>
                {fileUrl ? (
                  <Button  
                    variant="primary"
                    borderRadius="30px" 
                    as="a" 
                    href={fileUrl} 
                    download 
                    color="white"
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    {t('profilePhotosVideos.download')||'Download'}
                  </Button>
                ) : null}
                    </Stack>
                  )}
                </Box>
                )}
              </ModalBody>
            </ModalContent>
          </Modal>
        ) : null}
      </Stack>
    </>
  );
};
